/**
 * Leaderboard — ranked table of template x model config combinations.
 *
 * Uses Convex reactive subscriptions to fetch executions and claudeSessions,
 * joins via correlationId from execution metadata, and groups by
 * promptTemplateId + modelConfig. Each row shows completion rate, cost,
 * duration, session count, and needs_human frequency.
 *
 * Route: #/leaderboard (top-level page)
 */
import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

// --- Helpers -----------------------------------------------------------------

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remS = s % 60;
  if (m < 60) return `${m}m ${remS}s`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return `${h}h ${remM}m`;
}

function formatCost(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

// --- Types -------------------------------------------------------------------

interface LeaderboardRow {
  /** Template name from metadata, or "unknown" if not set */
  templateName: string;
  /** Template version from metadata, or null */
  templateVersion: number | null;
  /** Model config string from metadata (e.g. "sonnet-advisor") */
  modelConfig: string;
  /** Total sessions matched via correlationId */
  sessionCount: number;
  /** Total executions in this group */
  executionCount: number;
  /** Percentage of executions that completed successfully */
  completionRate: number;
  /** Average total cost in USD across sessions */
  avgCostUsd: number;
  /** Average executor token cost (input + output, excluding advisor) */
  avgExecutorCost: number;
  /** Average advisor token cost (advisorInput + advisorOutput) */
  avgAdvisorCost: number;
  /** Average session duration in ms */
  avgDurationMs: number;
  /** Number of executions that ended in needs_human */
  needsHumanCount: number;
  /** needs_human frequency as a percentage */
  needsHumanRate: number;
}

// --- Leaderboard computation -------------------------------------------------

function computeLeaderboard(
  executions: any[] | undefined,
  sessions: any[] | undefined,
  templates: any[] | undefined,
): LeaderboardRow[] {
  if (!executions || !sessions) return [];

  // Build session lookup by correlationId
  const sessionByCorrelation = new Map<string, any[]>();
  for (const s of sessions) {
    if (s.correlationId) {
      const list = sessionByCorrelation.get(s.correlationId) ?? [];
      list.push(s);
      sessionByCorrelation.set(s.correlationId, list);
    }
  }

  // Build template name lookup by ID (string representation)
  const templateNameById = new Map<string, { name: string; version: number }>();
  if (templates) {
    for (const t of templates) {
      templateNameById.set(t._id, { name: t.name, version: t.version });
    }
  }

  // Group executions by (promptTemplateId, modelConfig)
  type GroupKey = string;
  interface GroupData {
    templateName: string;
    templateVersion: number | null;
    modelConfig: string;
    executions: any[];
    sessions: any[];
  }

  const groups = new Map<GroupKey, GroupData>();

  for (const exec of executions) {
    const meta = exec.metadata ?? {};
    const templateId = meta.promptTemplateId ?? "none";
    const modelConfig = meta.modelConfig ?? "unknown";
    const key: GroupKey = `${templateId}::${modelConfig}`;

    if (!groups.has(key)) {
      const tmpl = templateNameById.get(templateId);
      groups.set(key, {
        templateName: tmpl?.name ?? (templateId === "none" ? "(no template)" : templateId),
        templateVersion: tmpl?.version ?? null,
        modelConfig,
        executions: [],
        sessions: [],
      });
    }

    const group = groups.get(key)!;
    group.executions.push(exec);

    // Join sessions via correlationId from execution metadata
    const correlationId = meta.correlationId;
    if (correlationId) {
      const matched = sessionByCorrelation.get(correlationId) ?? [];
      for (const s of matched) {
        // Avoid duplicates if multiple executions share same correlationId
        if (!group.sessions.some((existing) => existing._id === s._id)) {
          group.sessions.push(s);
        }
      }
    }
  }

  // Compute metrics per group
  const rows: LeaderboardRow[] = [];

  for (const group of groups.values()) {
    const { executions: groupExecs, sessions: groupSessions } = group;
    const completedCount = groupExecs.filter((e) => e.status === "completed").length;
    const needsHumanCount = groupExecs.filter(
      (e) => e.error?.includes("NEEDS_HUMAN") || e.status === "needs_human",
    ).length;

    // Session cost aggregation
    let totalCostUsd = 0;
    let totalExecutorCost = 0;
    let totalAdvisorCost = 0;
    let totalDurationMs = 0;
    let sessionsWithCost = 0;
    let sessionsWithDuration = 0;

    for (const s of groupSessions) {
      if (s.totalCostUsd != null) {
        totalCostUsd += s.totalCostUsd;
        sessionsWithCost++;
      }

      // Executor tokens = total tokens minus advisor tokens
      const advisorIn = s.advisorInputTokens ?? 0;
      const advisorOut = s.advisorOutputTokens ?? 0;
      const executorIn = s.totalInputTokens - advisorIn;
      const executorOut = s.totalOutputTokens - advisorOut;

      // Rough cost split: use total cost and token proportions
      const totalTokens = s.totalInputTokens + s.totalOutputTokens;
      if (totalTokens > 0 && s.totalCostUsd != null) {
        const advisorTokens = advisorIn + advisorOut;
        const executorTokens = executorIn + executorOut;
        totalExecutorCost += s.totalCostUsd * (executorTokens / totalTokens);
        totalAdvisorCost += s.totalCostUsd * (advisorTokens / totalTokens);
      }

      if (s.totalDurationMs != null) {
        totalDurationMs += s.totalDurationMs;
        sessionsWithDuration++;
      }
    }

    rows.push({
      templateName: group.templateName,
      templateVersion: group.templateVersion,
      modelConfig: group.modelConfig,
      sessionCount: groupSessions.length,
      executionCount: groupExecs.length,
      completionRate: groupExecs.length > 0 ? (completedCount / groupExecs.length) * 100 : 0,
      avgCostUsd: sessionsWithCost > 0 ? totalCostUsd / sessionsWithCost : 0,
      avgExecutorCost: sessionsWithCost > 0 ? totalExecutorCost / sessionsWithCost : 0,
      avgAdvisorCost: sessionsWithCost > 0 ? totalAdvisorCost / sessionsWithCost : 0,
      avgDurationMs: sessionsWithDuration > 0 ? totalDurationMs / sessionsWithDuration : 0,
      needsHumanCount,
      needsHumanRate: groupExecs.length > 0 ? (needsHumanCount / groupExecs.length) * 100 : 0,
    });
  }

  // Sort by completion rate descending, then by session count descending
  rows.sort((a, b) => {
    if (b.completionRate !== a.completionRate) return b.completionRate - a.completionRate;
    return b.sessionCount - a.sessionCount;
  });

  return rows;
}

// --- Model config badge styling ----------------------------------------------

const modelConfigColors: Record<string, string> = {
  "opus-solo": "bg-purple-500/15 text-purple-400 border-purple-500/30",
  "sonnet-advisor": "bg-blue-500/15 text-blue-400 border-blue-500/30",
  "haiku-advisor": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "sonnet-solo": "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
};

function ModelConfigBadge({ config }: { config: string }) {
  const colorCls = modelConfigColors[config] ?? "bg-zinc-700/40 text-zinc-400 border-zinc-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded border ${colorCls}`}>
      {config}
    </span>
  );
}

// --- Completion rate bar -----------------------------------------------------

function CompletionBar({ rate }: { rate: number }) {
  const color = rate >= 80 ? "bg-green-500" : rate >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden max-w-[60px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(rate, 100)}%` }} />
      </div>
      <span className="text-xs tabular-nums">{rate.toFixed(0)}%</span>
    </div>
  );
}

// --- Leaderboard page --------------------------------------------------------

export function LeaderboardPage() {
  // Fetch all executions across all projects for the leaderboard
  // NOTE: executions.list requires a projectId; for a global leaderboard we
  // fetch all projects and then all executions per project.
  // Alternative: use a dedicated listAll query if available.
  const projects = useQuery(api.projects.list, {});
  const sessions = useQuery(api.claudeSessions.list);
  const templates = useQuery(api.promptTemplates.listTemplates);

  // Collect executions from all projects
  // We query executions for every project. Since useQuery is reactive and
  // must be called unconditionally, we use a multi-project approach with
  // a single component that batches the queries.
  // For simplicity, we use a child component pattern to gather executions.

  const loading = projects === undefined || sessions === undefined || templates === undefined;

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Leaderboard</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Template and model config performance comparison.
          </p>
        </div>
        <div className="text-sm text-zinc-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-zinc-100">Leaderboard</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Performance ranked by template and model config. Real-time via Convex subscriptions.
        </p>
      </div>
      {projects.length === 0 ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-sm">No PRD executions yet.</p>
          <p className="text-xs text-zinc-500 mt-1">
            Dispatch an agent session to start collecting leaderboard data.
          </p>
        </div>
      ) : (
        <LeaderboardTable
          projectIds={projects.map((p) => p._id)}
          sessions={sessions}
          templates={templates}
        />
      )}
    </div>
  );
}

// --- Inner component that queries executions per project ---------------------

function LeaderboardTable({
  projectIds,
  sessions,
  templates,
}: {
  projectIds: string[];
  sessions: any[];
  templates: any[];
}) {
  // Query executions for each project. We'll query the first N projects
  // to avoid too many subscriptions. In practice, most deployments have
  // a small number of projects.
  const MAX_PROJECTS = 20;
  const limitedIds = projectIds.slice(0, MAX_PROJECTS);

  // We need to call useQuery unconditionally for each project slot.
  // Use a fixed array of query hooks.
  const e0 = useQuery(api.executions.list, limitedIds[0] ? { projectId: limitedIds[0] as any } : "skip");
  const e1 = useQuery(api.executions.list, limitedIds[1] ? { projectId: limitedIds[1] as any } : "skip");
  const e2 = useQuery(api.executions.list, limitedIds[2] ? { projectId: limitedIds[2] as any } : "skip");
  const e3 = useQuery(api.executions.list, limitedIds[3] ? { projectId: limitedIds[3] as any } : "skip");
  const e4 = useQuery(api.executions.list, limitedIds[4] ? { projectId: limitedIds[4] as any } : "skip");
  const e5 = useQuery(api.executions.list, limitedIds[5] ? { projectId: limitedIds[5] as any } : "skip");
  const e6 = useQuery(api.executions.list, limitedIds[6] ? { projectId: limitedIds[6] as any } : "skip");
  const e7 = useQuery(api.executions.list, limitedIds[7] ? { projectId: limitedIds[7] as any } : "skip");
  const e8 = useQuery(api.executions.list, limitedIds[8] ? { projectId: limitedIds[8] as any } : "skip");
  const e9 = useQuery(api.executions.list, limitedIds[9] ? { projectId: limitedIds[9] as any } : "skip");
  const e10 = useQuery(api.executions.list, limitedIds[10] ? { projectId: limitedIds[10] as any } : "skip");
  const e11 = useQuery(api.executions.list, limitedIds[11] ? { projectId: limitedIds[11] as any } : "skip");
  const e12 = useQuery(api.executions.list, limitedIds[12] ? { projectId: limitedIds[12] as any } : "skip");
  const e13 = useQuery(api.executions.list, limitedIds[13] ? { projectId: limitedIds[13] as any } : "skip");
  const e14 = useQuery(api.executions.list, limitedIds[14] ? { projectId: limitedIds[14] as any } : "skip");
  const e15 = useQuery(api.executions.list, limitedIds[15] ? { projectId: limitedIds[15] as any } : "skip");
  const e16 = useQuery(api.executions.list, limitedIds[16] ? { projectId: limitedIds[16] as any } : "skip");
  const e17 = useQuery(api.executions.list, limitedIds[17] ? { projectId: limitedIds[17] as any } : "skip");
  const e18 = useQuery(api.executions.list, limitedIds[18] ? { projectId: limitedIds[18] as any } : "skip");
  const e19 = useQuery(api.executions.list, limitedIds[19] ? { projectId: limitedIds[19] as any } : "skip");

  const allResults = [e0, e1, e2, e3, e4, e5, e6, e7, e8, e9, e10, e11, e12, e13, e14, e15, e16, e17, e18, e19];

  const allExecutions = useMemo(() => {
    const combined: any[] = [];
    for (let i = 0; i < limitedIds.length; i++) {
      const result = allResults[i];
      if (result) {
        combined.push(...result);
      }
    }
    return combined;
  }, [limitedIds.length, ...allResults]);

  const rows = useMemo(
    () => computeLeaderboard(allExecutions, sessions, templates),
    [allExecutions, sessions, templates],
  );

  const someLoading = limitedIds.some((_, i) => allResults[i] === undefined);

  if (rows.length === 0 && !someLoading) {
    return (
      <div className="text-center py-16 text-zinc-600">
        <p className="text-sm">No execution data with metadata yet.</p>
        <p className="text-xs text-zinc-500 mt-1">
          Executions need correlationId and promptTemplateId in their metadata to appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {someLoading && (
        <div className="text-xs text-zinc-600">Loading execution data...</div>
      )}

      {/* Mobile card layout */}
      <div className="sm:hidden space-y-3">
        {rows.map((row, i) => (
          <div key={`${row.templateName}-${row.modelConfig}`} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-mono text-zinc-500">#{i + 1}</span>
                <span className="text-sm font-semibold text-zinc-100 truncate">{row.templateName}</span>
                {row.templateVersion != null && (
                  <span className="text-xs text-zinc-500 font-mono shrink-0">v{row.templateVersion}</span>
                )}
              </div>
              <ModelConfigBadge config={row.modelConfig} />
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500">Completion</span>
                <div className="mt-0.5">
                  <CompletionBar rate={row.completionRate} />
                </div>
              </div>
              <div>
                <span className="text-zinc-500">Sessions</span>
                <p className="text-zinc-300 font-mono mt-0.5">{row.sessionCount}</p>
              </div>
              <div>
                <span className="text-zinc-500">Avg Cost</span>
                <p className="text-zinc-300 font-mono mt-0.5">{formatCost(row.avgCostUsd)}</p>
              </div>
              <div>
                <span className="text-zinc-500">Avg Duration</span>
                <p className="text-zinc-300 font-mono mt-0.5">
                  {row.avgDurationMs > 0 ? formatDuration(row.avgDurationMs) : "-"}
                </p>
              </div>
              <div>
                <span className="text-zinc-500">Executor Cost</span>
                <p className="text-zinc-300 font-mono mt-0.5">{formatCost(row.avgExecutorCost)}</p>
              </div>
              <div>
                <span className="text-zinc-500">Advisor Cost</span>
                <p className="text-zinc-300 font-mono mt-0.5">{formatCost(row.avgAdvisorCost)}</p>
              </div>
              <div>
                <span className="text-zinc-500">Needs Human</span>
                <p className="text-zinc-300 font-mono mt-0.5">
                  {row.needsHumanCount} ({row.needsHumanRate.toFixed(0)}%)
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-900/50 text-zinc-500 text-xs uppercase tracking-wider">
              <th className="px-4 py-2.5 text-left w-8">#</th>
              <th className="px-4 py-2.5 text-left">Template</th>
              <th className="px-4 py-2.5 text-left">Model Config</th>
              <th className="px-4 py-2.5 text-center">Sessions</th>
              <th className="px-4 py-2.5 text-left">Completion</th>
              <th className="px-4 py-2.5 text-right">Avg Cost</th>
              <th className="px-4 py-2.5 text-right">Executor</th>
              <th className="px-4 py-2.5 text-right">Advisor</th>
              <th className="px-4 py-2.5 text-right">Avg Duration</th>
              <th className="px-4 py-2.5 text-center">Needs Human</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {rows.map((row, i) => (
              <tr
                key={`${row.templateName}-${row.modelConfig}`}
                className="hover:bg-zinc-900/30"
              >
                <td className="px-4 py-2.5 text-zinc-600 font-mono text-xs">{i + 1}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-zinc-200 truncate max-w-[180px]">{row.templateName}</span>
                    {row.templateVersion != null && (
                      <span className="text-xs text-zinc-600 font-mono">v{row.templateVersion}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <ModelConfigBadge config={row.modelConfig} />
                </td>
                <td className="px-4 py-2.5 text-center tabular-nums text-zinc-400">
                  {row.sessionCount}
                </td>
                <td className="px-4 py-2.5">
                  <CompletionBar rate={row.completionRate} />
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-zinc-400 font-mono text-xs">
                  {formatCost(row.avgCostUsd)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-zinc-500 font-mono text-xs">
                  {formatCost(row.avgExecutorCost)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-zinc-500 font-mono text-xs">
                  {formatCost(row.avgAdvisorCost)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-zinc-400 text-xs">
                  {row.avgDurationMs > 0 ? formatDuration(row.avgDurationMs) : "-"}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {row.needsHumanCount > 0 ? (
                    <span className="text-amber-400 text-xs tabular-nums">
                      {row.needsHumanCount} ({row.needsHumanRate.toFixed(0)}%)
                    </span>
                  ) : (
                    <span className="text-zinc-600 text-xs">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {projectIds.length > MAX_PROJECTS && (
        <p className="text-xs text-zinc-600">
          Showing data from {MAX_PROJECTS} of {projectIds.length} PRD executions.
        </p>
      )}
    </div>
  );
}
